import { Injectable, inject, ChangeDetectorRef, NgZone } from '@angular/core';
import { QuestionView, QuestionType, QuestionTypeMapper, generateGuid } from '../models';
import { FormStore } from '../store/form.store';

export interface QuestionManagementCallbacks {
  getQuestions: () => QuestionView[];
  setQuestions: (questions: QuestionView[]) => void;
  isReadOnly: () => boolean;
  setPaletteQuestionId: (id: string | null) => void;
  setShowPalette: (show: boolean) => void;
}

/**
 * Service to handle question CRUD operations, element selection,
 * and question type mapping for the form builder.
 */
@Injectable({
  providedIn: 'root'
})
export class QuestionManagementService {
  private formStore = inject(FormStore);

  /** Maps FormElement id to QuestionType enum */
  mapElementToQuestionType(elementId: string): QuestionType | undefined {
    const typeMap: Record<string, QuestionType> = {
      'checkbox': QuestionType.CheckboxList,
      'checkbox-list': QuestionType.CheckboxList,
      'dropdown': QuestionType.DropdownList,
      'dropdown-list': QuestionType.DropdownList,
      'dynamic-dropdown': QuestionType.DynamicDropdown,
      'dynamicdropdown': QuestionType.DynamicDropdown,
      'email': QuestionType.Email,
      'date': QuestionType.Date,
      'textbox': QuestionType.TextBox,
      'input-box': QuestionType.TextBox,
      'phone': QuestionType.Tel,
      'phone-number': QuestionType.Tel,
      'tel': QuestionType.Tel,
      'radio': QuestionType.Radio,
      'radio-list': QuestionType.Radio,
      'textarea': QuestionType.TextArea,
      'text-area': QuestionType.TextArea,
      'toggle': QuestionType.Toggle,
      'confirmation': QuestionType.Confirmation,
      'confirm': QuestionType.Confirmation,
      'section': QuestionType.Section,
      'title': QuestionType.Title,
      'helpertext': QuestionType.HelperText
    };
    return typeMap[elementId];
  }

  deleteQuestion(questionId: string): void {
    this.formStore.deleteQuestion(questionId);
  }

  insertBelow(
    questionId: string,
    callbacks: QuestionManagementCallbacks
  ): void {
    const currentIndex = callbacks.getQuestions().findIndex(q => q.id === questionId);
    if (currentIndex === -1) return;

    const newId = generateGuid();
    const newQuestion: QuestionView = {
      id: newId,
      labelText: '',
      type: '',
      isRequired: false,
      showMenu: false,
      isEditing: true,
      order: currentIndex + 1,
      questionNumber: undefined
    };

    const updatedQuestions = [...callbacks.getQuestions()];
    updatedQuestions.splice(currentIndex + 1, 0, newQuestion);
    const reorderedQuestions = updatedQuestions.map((q, index) => ({ ...q, order: index }));

    this.formStore.reorderQuestions(reorderedQuestions);
    callbacks.setPaletteQuestionId(newId);
    callbacks.setShowPalette(true);

    setTimeout(() => {
      const element = document.querySelector(`[data-question-id="${newId}"]`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }

  addNew(callbacks: QuestionManagementCallbacks): void {
    const newId = generateGuid();
    const newQuestion: QuestionView = {
      id: newId,
      labelText: '',
      type: '',
      isRequired: false,
      showMenu: false,
      isEditing: true,
      order: callbacks.getQuestions().length,
      questionNumber: undefined
    };

    this.formStore.addQuestion(newQuestion);
    callbacks.setPaletteQuestionId(newId);
    callbacks.setShowPalette(true);

    setTimeout(() => {
      const element = document.querySelector(`[data-question-id="${newId}"]`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }

  toggleRequired(questionId: string, questions: QuestionView[]): void {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const newIsRequired = !question.isRequired;
    const updatedValidators = { ...(question.validators || {}) };
    if (newIsRequired) {
      // API contract: validators is a Dictionary<string, string> — store as string.
      updatedValidators['required'] = 'true';
    } else {
      delete updatedValidators['required'];
    }
    this.formStore.updateQuestion({ ...question, isRequired: newIsRequired, validators: updatedValidators });
  }

  acceptQuestion(questionId: string, questions: QuestionView[]): void {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      this.formStore.updateQuestion({ ...question, isEditing: false });
    }
  }

  editQuestion(questionId: string, questions: QuestionView[]): void {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      this.formStore.updateQuestion({ ...question, isEditing: true });
    }
  }

  cancelPalette(callbacks: QuestionManagementCallbacks, paletteQuestionId: string | null): void {
    if (paletteQuestionId) {
      const question = callbacks.getQuestions().find(q => q.id === paletteQuestionId);
      if (question && !question.questionType) {
        const updatedQuestions = callbacks.getQuestions().filter(q => q.id !== paletteQuestionId);
        callbacks.setQuestions(updatedQuestions);
        this.formStore.deleteQuestion(paletteQuestionId);
      }
    }
    callbacks.setShowPalette(false);
    callbacks.setPaletteQuestionId(null);
  }

  /**
   * Handle selection of a new element type from the palette.
   * Updates an existing question or creates a new one with the selected type.
   */
  onNewElementSelected(
    elementId: string,
    isReportable: boolean,
    isNewElement: boolean,
    callbacks: QuestionManagementCallbacks,
    paletteQuestionId: string | null,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): boolean {
    const questionType = this.mapElementToQuestionType(elementId);
    if (!questionType) {
      console.warn('Unknown element type:', elementId);
      callbacks.setShowPalette(false);
      callbacks.setPaletteQuestionId(null);
      return false;
    }

    if (paletteQuestionId !== null) {
      this.updateExistingQuestionType(paletteQuestionId, questionType, isReportable, isNewElement, callbacks);
    } else {
      this.createNewQuestion(questionType, isReportable, isNewElement, callbacks, ngZone, cdr);
    }

    callbacks.setShowPalette(false);
    callbacks.setPaletteQuestionId(null);
    return true;
  }

  private updateExistingQuestionType(
    questionId: string,
    questionType: QuestionType,
    isReportable: boolean,
    isNewElement: boolean,
    callbacks: QuestionManagementCallbacks
  ): void {
    const question = callbacks.getQuestions().find(q => q.id === questionId);
    if (!question) return;

    const isStructural = questionType === QuestionType.Section || questionType === QuestionType.Title || questionType === QuestionType.HelperText;
    const typeString = QuestionTypeMapper.mapQuestionTypeToApiType(questionType);
    const wasHelperText = question.questionType === QuestionType.HelperText;
    const isNowHelperText = questionType === QuestionType.HelperText;

    let updatedLabelText = question.labelText;
    if (wasHelperText && !isNowHelperText) {
      updatedLabelText = '';
    } else if (isNowHelperText && !question.labelText) {
      updatedLabelText = 'Helper Text';
    }

    const updatedQuestion = {
      ...question,
      type: typeString,
      questionType: questionType,
      questionNumber: isStructural ? undefined : question.questionNumber,
      labelText: updatedLabelText,
      helperText: questionType === QuestionType.HelperText ? undefined : question.helperText,
      // The palette's Reportable tick applies whether the question is being
      // added from scratch or given its type here; without this it was read,
      // emitted and then dropped on this path.
      isReportable,
      isNewElement
    };
    this.formStore.updateQuestion(updatedQuestion);
  }

  private createNewQuestion(
    questionType: QuestionType,
    isReportable: boolean,
    isNewElement: boolean,
    callbacks: QuestionManagementCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    const currentQuestions = callbacks.getQuestions();
    const newId = generateGuid();
    const typeString = QuestionTypeMapper.mapQuestionTypeToApiType(questionType);
    const newQuestion: QuestionView = {
      id: newId,
      labelText: questionType === QuestionType.HelperText ? 'Helper Text' : '',
      type: typeString,
      isRequired: false,
      showMenu: false,
      isEditing: true,
      questionType: questionType,
      order: currentQuestions.length,
      questionNumber: undefined,
      sectionId: undefined,
      isReportable,
      isNewElement
    };

    callbacks.setQuestions([...currentQuestions, newQuestion]);
    ngZone.run(() => { cdr.markForCheck(); cdr.detectChanges(); });

    this.formStore.addQuestion(newQuestion);
    this.syncAfterAdd(newId, callbacks, ngZone, cdr);
  }

  /**
   * Handle selection of an existing form element from the palette.
   * Maps all properties from the existing element to a question.
   */
  onExistingElementSelected(
    existingData: Record<string, any>,
    elementId: string,
    elementName: string,
    elementLabelText: string | undefined,
    callbacks: QuestionManagementCallbacks,
    paletteQuestionId: string | null,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    const existingElementId = existingData['formElementId'] ?? existingData['id'] ?? elementId;

    // Try to map the type
    let questionType: QuestionType | undefined = QuestionTypeMapper.mapApiTypeToQuestionType(existingData['type']) ?? undefined;
    if (!questionType) {
      questionType = this.mapElementToQuestionType(elementId);
    }
    if (!questionType) {
      console.warn('Could not map type for existing element:', existingData['type']);
      callbacks.setShowPalette(false);
      callbacks.setPaletteQuestionId(null);
      return;
    }

    const typeString = QuestionTypeMapper.mapQuestionTypeToApiType(questionType) || existingData['type'] || '';

    const labelText = existingData['labelText'] || existingData['questionText'] || elementLabelText ||
      existingData['text'] || existingData['label'] || existingData['question'] || elementName || 'Untitled Question';

    const optionsArray = this.extractOptions(existingData);
    const additionalInfo = existingData['additionalInfo'] || '';
    const helperText = existingData['helperText'] || '';
    const isRequired = existingData['isRequired'] === true || existingData['required'] === true || existingData['required'] === 'true';
    const isStructural = questionType === QuestionType.Section || questionType === QuestionType.Title || questionType === QuestionType.HelperText;

    const commonProps = {
      labelText, type: typeString, isRequired, questionType,
      additionalInfo, helperText, options: optionsArray,
      validators: existingData['validators'] || {},
      tooltip: existingData['tooltip'] || '',
      placeholder: existingData['placeholder'] || '',
      semanticTags: existingData['semanticTags'] || []
    };

    if (paletteQuestionId !== null) {
      const question = callbacks.getQuestions().find(q => q.id === paletteQuestionId);
      if (question) {
        const updatedQuestion: QuestionView = {
          ...question, ...commonProps,
          id: existingElementId,
          questionNumber: isStructural ? undefined : question.questionNumber
        };
        if (existingElementId !== question.id) {
          const updatedQuestions = callbacks.getQuestions().map(q => q.id === question.id ? updatedQuestion : q);
          callbacks.setQuestions(updatedQuestions);
          this.formStore.reorderQuestions(updatedQuestions);
        } else {
          this.formStore.updateQuestion(updatedQuestion);
        }
      }
    } else {
      const currentQuestions = callbacks.getQuestions();
      const newQuestion: QuestionView = {
        id: existingElementId, ...commonProps,
        showMenu: false, isEditing: true,
        order: currentQuestions.length,
        questionNumber: undefined, sectionId: undefined,
        deleted: false, lastUpdatedOn: '0001-01-01T00:00:00', lastUpdatedBy: null
      };

      callbacks.setQuestions([...currentQuestions, newQuestion]);
      ngZone.run(() => { cdr.markForCheck(); cdr.detectChanges(); });
      this.formStore.addQuestion(newQuestion);
      this.syncAfterAdd(existingElementId, callbacks, ngZone, cdr);
    }

    callbacks.setShowPalette(false);
    callbacks.setPaletteQuestionId(null);
  }

  private extractOptions(data: Record<string, any>): string[] {
    const options = data['options'] || data['optionValues'] || data['choices'] || data['items'] || data['optionList'] || [];
    if (!Array.isArray(options)) return [];
    return options.map(opt => {
      if (typeof opt === 'string') return opt;
      if (typeof opt === 'object' && opt !== null) {
        return opt['value'] || opt['text'] || opt['label'] || opt['name'] || String(opt);
      }
      return String(opt);
    }).filter(opt => opt && opt.trim() !== '');
  }

  private syncAfterAdd(
    newId: string,
    callbacks: QuestionManagementCallbacks,
    ngZone: NgZone,
    cdr: ChangeDetectorRef
  ): void {
    // The question was added via a synchronous patchState before this call,
    // so the store signal already reflects it.
    const updatedQuestions = this.formStore.questions();
    callbacks.setQuestions([...updatedQuestions]);
    ngZone.run(() => {
      cdr.markForCheck();
      cdr.detectChanges();
      setTimeout(() => {
        const el = document.querySelector(`[data-question-id="${newId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    });
  }
}
